import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useApi } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { ChevronLeft, CircleDot, Circle } from "lucide-react";
import RichTextEditor from "@/components/RichTextEditor";
import OrganiserDropdown, { displayName as organiserDisplay } from "@/components/OrganiserDropdown";
import MenuConfigEditor, {
  createEmptyMenuConfig, menuConfigFromRaw, validateMenuConfig,
} from "@/components/MenuConfigEditor";
import ComplimentaryCodesPanel from "@/components/ComplimentaryCodesPanel";

function stripHtml(html) { return String(html || "").replace(/<[^>]*>/g, "").trim(); }

/** Convert an ISO date to the `YYYY-MM-DDTHH:mm` shape expected by
 *  <input type="datetime-local">. Returns "" if no valid date. */
function toLocalInput(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "";
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch { return ""; }
}

function SectionCard({ title, hint, children, testId }) {
  return (
    <Card className="p-5 mb-4" data-testid={testId}>
      <div className="text-[11px] uppercase tracking-widest text-primary mb-1">{title}</div>
      {hint && <div className="text-xs text-muted-foreground mb-3">{hint}</div>}
      {children}
    </Card>
  );
}

function OptionButton({ active, onClick, testId, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        "flex-1 px-4 py-2.5 rounded-md border-2 text-sm font-medium transition-colors",
        active ? "border-primary bg-primary/5 text-primary" : "border-border bg-white text-foreground hover:bg-muted/40",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

function HostRadio({ host, active, onClick, testId }) {
  const Icon = active ? CircleDot : Circle;
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={[
        "w-full flex items-center gap-3 px-3 py-2.5 rounded-md border transition-colors text-left",
        active ? "bg-primary/5 border-primary" : "bg-white border-border hover:bg-muted/40",
      ].join(" ")}
    >
      <Icon size={18} className="text-primary shrink-0" />
      <div className="flex-1">
        <div className="text-sm font-medium">{host.name}</div>
        <div className="text-xs text-muted-foreground">
          {host.host_id}
          {host.has_live_key === false && " · no live key yet"}
          {host.has_test_key === false && " · no test key yet"}
        </div>
      </div>
    </button>
  );
}

export default function AdminEventNewPage() {
  const api = useApi();
  const nav = useNavigate();
  const { id: editId } = useParams();
  const [sp] = useSearchParams();
  const templateId = sp.get("template");
  const isEdit = !!editId;

  // Mandatory
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [organiser, setOrganiser] = useState("");
  const [organiserUserId, setOrganiserUserId] = useState(null);
  const [organiserEmail, setOrganiserEmail] = useState("");
  const [additionalOrganisers, setAdditionalOrganisers] = useState([null, null]);
  const [location, setLocation] = useState("");
  const [dateStr, setDateStr] = useState("");
  const [bookingsCloseStr, setBookingsCloseStr] = useState("");
  const [price, setPrice] = useState("0");
  const [capacity, setCapacity] = useState("50");
  const [capacityVisible, setCapacityVisible] = useState(true);
  const [maxPlaces, setMaxPlaces] = useState("1");

  const [useMasonicTitle, setUseMasonicTitle] = useState(false);
  const [useGrandRank, setUseGrandRank] = useState(false);
  const [useProvincialRank, setUseProvincialRank] = useState(false);
  const [useDietaryConcerns, setUseDietaryConcerns] = useState(false);

  const [accountLabel, setAccountLabel] = useState("PGLM");
  const [eventHosts, setEventHosts] = useState([]);
  const [testMode, setTestMode] = useState(true);

  const [menuConfig, setMenuConfig] = useState(createEmptyMenuConfig());
  const [useBookingCustom, setUseBookingCustom] = useState(false);
  const [bookingCustomStr, setBookingCustomStr] = useState("");
  const [useAttendeeCustom, setUseAttendeeCustom] = useState(false);
  const [attendeeCustomStr, setAttendeeCustomStr] = useState("");

  const [complimentaryEnabled, setComplimentaryEnabled] = useState(false);
  const [saving, setSaving] = useState(false);
  const [eventTemplateId, setEventTemplateId] = useState(templateId || undefined);

  useEffect(() => {
    (async () => {
      const r = await api("/event-hosts");
      if (r.ok) { const d = await r.json(); setEventHosts(d.event_hosts || []); }
    })();
    // eslint-disable-next-line
  }, []);

  // Prefill from an EXISTING event (edit) or from a chosen TEMPLATE (new).
  useEffect(() => {
    (async () => {
      if (isEdit) {
        const r = await api(`/events/${editId}`);
        if (!r.ok) return;
        const { event: ev } = await r.json();
        setName(ev.name || "");
        setDescription(ev.description || "");
        setOrganiser(ev.organiser || "");
        setOrganiserUserId(ev.organiser_user_id || null);
        setOrganiserEmail(ev.organiser_email || "");
        setLocation(ev.location || "");
        setDateStr(toLocalInput(ev.starts_at));
        setBookingsCloseStr(toLocalInput(ev.bookings_close_at));
        setPrice(((ev.price_pence || 0) / 100).toString());
        setCapacity(String(ev.capacity || 50));
        setCapacityVisible(ev.capacity_visible !== false);
        setMaxPlaces(String(ev.max_places_per_booking || 1));
        setUseMasonicTitle(!!ev.use_masonic_title);
        setUseGrandRank(!!ev.use_grand_rank);
        setUseProvincialRank(!!ev.use_provincial_rank);
        setUseDietaryConcerns(!!ev.use_dietary_concerns);
        setAccountLabel(ev.event_host_id || ev.stripe_account_label || "PGLM");
        setTestMode(ev.test_mode !== false);
        setComplimentaryEnabled(!!ev.complimentary_codes_enabled);
        setMenuConfig(menuConfigFromRaw(ev.menu_config));
        const cfs = ev.custom_fields || [];
        const bk = cfs.filter((c) => (c.scope || "booking") !== "attendee");
        const at = cfs.filter((c) => c.scope === "attendee");
        setUseBookingCustom(bk.length > 0);
        setBookingCustomStr(bk.map((c) => c.name).join(", "));
        setUseAttendeeCustom(at.length > 0);
        setAttendeeCustomStr(at.map((c) => c.name).join(", "));
        setEventTemplateId(ev.template_id || undefined);
        // Load names for additional organiser slots
        const addIds = Array.isArray(ev.additional_organiser_user_ids) ? ev.additional_organiser_user_ids.slice(0, 2) : [];
        if (addIds.length) {
          const r2 = await api("/admin/organiser-candidates");
          if (r2.ok) {
            const { candidates } = await r2.json();
            const map = new Map((candidates || []).map((u) => [u.user_id, u]));
            setAdditionalOrganisers([0, 1].map((i) => {
              const uid = addIds[i]; if (!uid) return null;
              const u = map.get(uid); return u ? { user_id: uid, label: organiserDisplay(u) } : null;
            }));
          }
        }
        return;
      }
      if (templateId) {
        const r = await api(`/templates/${templateId}`);
        if (r.ok) {
          const { template } = await r.json();
          setName(template.name || "");
          setDescription(template.description || "");
          setOrganiser(template.default_organiser || "");
          setOrganiserUserId(template.default_organiser_user_id || null);
          setLocation(template.default_location || "");
          setPrice(((template.default_price_pence || 0) / 100).toString());
          setCapacity(String(template.default_capacity || 50));
          setMaxPlaces(String(template.max_places_per_booking || 1));
          setUseMasonicTitle(!!template.use_masonic_title);
          setUseGrandRank(!!template.use_grand_rank);
          setUseProvincialRank(!!template.use_provincial_rank);
          setUseDietaryConcerns(!!template.use_dietary_concerns);
          setAccountLabel(template.event_host_id || template.stripe_account_label || "PGLM");
          setMenuConfig(menuConfigFromRaw(template.menu_config));
          const cfs = template.custom_fields || [];
          const bk = cfs.filter((c) => (c.scope || "booking") !== "attendee");
          const at = cfs.filter((c) => c.scope === "attendee");
          setUseBookingCustom(bk.length > 0);
          setBookingCustomStr(bk.map((c) => c.name).join(", "));
          setUseAttendeeCustom(at.length > 0);
          setAttendeeCustomStr(at.map((c) => c.name).join(", "));
          setComplimentaryEnabled(!!template.complimentary_codes_enabled);
          setEventTemplateId(templateId);
        }
      }
    })();
    // eslint-disable-next-line
  }, [editId, templateId]);

  const validate = () => {
    if (!name.trim()) return "Event name is required.";
    if (!stripHtml(description)) return "Description is required.";
    if (!organiser.trim()) return "Primary organiser is required.";
    if (!location.trim()) return "Location is required.";
    if (!dateStr) return "Event date & time is required.";
    if (!price.trim() || isNaN(parseFloat(price))) return "Price is required (use 0 for free).";
    const cap = parseInt(capacity || "0", 10);
    if (!cap || cap < 1) return "Capacity must be at least 1.";
    const mp = parseInt(maxPlaces || "0", 10);
    if (!mp || mp < 1 || mp > 12) return "Max places per booking must be between 1 and 12.";
    const menuErr = validateMenuConfig(menuConfig);
    if (menuErr) return menuErr;
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) return toast.error(err);
    setSaving(true);
    try {
      const parseCf = (str, scope) => str.split(",").map((s) => s.trim()).filter(Boolean)
        .map((label) => ({ name: label, type: "text", required: false, scope }));
      const bookingFields = useBookingCustom ? parseCf(bookingCustomStr, "booking") : [];
      const attendeeFields = useAttendeeCustom ? parseCf(attendeeCustomStr, "attendee") : [];
      const body = {
        template_id: eventTemplateId,
        name: name.trim(),
        description,
        organiser: organiser.trim(),
        organiser_user_id: organiserUserId,
        additional_organiser_user_ids: additionalOrganisers.filter((o) => o?.user_id).map((o) => o.user_id),
        organiser_email: organiserEmail.trim(),
        use_masonic_title: useMasonicTitle,
        use_grand_rank: useGrandRank,
        use_provincial_rank: useProvincialRank,
        use_dietary_concerns: useDietaryConcerns,
        location: location.trim(),
        starts_at: new Date(dateStr).toISOString(),
        bookings_close_at: bookingsCloseStr ? new Date(bookingsCloseStr).toISOString() : null,
        price_pence: Math.max(0, Math.round(parseFloat(price || "0") * 100)),
        capacity: Math.max(1, parseInt(capacity || "50", 10)),
        capacity_visible: capacityVisible,
        max_places_per_booking: Math.max(1, Math.min(12, parseInt(maxPlaces || "1", 10))),
        menu_config: menuConfig,
        menu_choices: menuConfig.mode === "simple" ? menuConfig.meal_options : [],
        custom_fields: [...bookingFields, ...attendeeFields],
        stripe_account_label: accountLabel,
        event_host_id: accountLabel,
        test_mode: testMode,
        complimentary_codes_enabled: complimentaryEnabled,
        currency: "gbp",
      };
      const r = await api(isEdit ? `/events/${editId}` : "/events", {
        method: isEdit ? "PATCH" : "POST", body: JSON.stringify(body),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) return toast.error(d.detail || "Save failed");
      toast.success(isEdit ? "Event updated" : "Event published");
      nav("/admin");
    } finally { setSaving(false); }
  };

  const setAdditional = (idx, u) => setAdditionalOrganisers((prev) => {
    const next = [...prev];
    next[idx] = u ? { user_id: u.user_id, label: organiserDisplay(u) } : null;
    return next;
  });

  return (
    <div>
      <button onClick={() => nav(-1)} className="text-sm text-primary inline-flex items-center gap-1 mb-4" data-testid="event-back">
        <ChevronLeft size={16} /> Back
      </button>
      <PageHeader title={isEdit ? "Edit event" : "New event"} testId="event-form-header" />

      <SectionCard title="Required details" hint="All fields below are mandatory." testId="ev-required-section">
        <div className="space-y-3">
          <div className="space-y-1">
            <Label>Event name <span className="text-destructive">*</span></Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Annual Festival Dinner 2027" data-testid="ev-name" />
          </div>
          <div className="space-y-1">
            <Label>Description <span className="text-destructive">*</span></Label>
            <RichTextEditor value={description} onChange={setDescription} testId="ev-desc" placeholder="Public description shown to members" />
          </div>
          <OrganiserDropdown
            label="Primary organiser"
            required
            value={organiser}
            selectedUserId={organiserUserId}
            testId="ev-organiser"
            hint="Only members promoted to Organiser or Admin appear in the list."
            onSelect={(u) => {
              if (u) { setOrganiser(organiserDisplay(u)); setOrganiserUserId(u.user_id); if (u.email && !organiserEmail) setOrganiserEmail(u.email); }
              else { setOrganiser(""); setOrganiserUserId(null); }
            }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <OrganiserDropdown
              label="Additional organiser (optional)"
              value={additionalOrganisers[0]?.label || ""}
              selectedUserId={additionalOrganisers[0]?.user_id || null}
              testId="ev-organiser-2"
              onSelect={(u) => setAdditional(0, u)}
            />
            <OrganiserDropdown
              label="Additional organiser (optional)"
              value={additionalOrganisers[1]?.label || ""}
              selectedUserId={additionalOrganisers[1]?.user_id || null}
              testId="ev-organiser-3"
              onSelect={(u) => setAdditional(1, u)}
            />
          </div>
          <div className="space-y-1">
            <Label>Alternate contact email (optional)</Label>
            <Input type="email" value={organiserEmail} onChange={(e) => setOrganiserEmail(e.target.value)} placeholder="events@pglm.org.uk" data-testid="ev-organiser-email" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label>Starts at <span className="text-destructive">*</span></Label>
              <Input type="datetime-local" value={dateStr} onChange={(e) => setDateStr(e.target.value)} data-testid="ev-starts" />
            </div>
            <div className="space-y-1">
              <Label>Bookings close at</Label>
              <Input type="datetime-local" value={bookingsCloseStr} onChange={(e) => setBookingsCloseStr(e.target.value)} data-testid="ev-cutoff" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Location <span className="text-destructive">*</span></Label>
              <Input value={location} onChange={(e) => setLocation(e.target.value)} placeholder="Freemasons' Hall, London" data-testid="ev-location" />
            </div>
            <div className="space-y-1">
              <Label>Price (£) <span className="text-destructive">*</span></Label>
              <Input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} data-testid="ev-price" />
            </div>
            <div className="space-y-1">
              <Label>Max places per booking (1-12) <span className="text-destructive">*</span></Label>
              <Input inputMode="numeric" value={maxPlaces} onChange={(e) => setMaxPlaces(e.target.value)} data-testid="ev-max-places" />
            </div>
            <div className="space-y-1 sm:col-span-2">
              <Label>Capacity <span className="text-destructive">*</span></Label>
              <div className="flex items-center gap-3">
                <Input inputMode="numeric" value={capacity} onChange={(e) => setCapacity(e.target.value)} className="max-w-[160px]" data-testid="ev-capacity" />
                <label className="inline-flex items-center gap-2 text-sm">
                  <Checkbox checked={capacityVisible} onCheckedChange={(v) => setCapacityVisible(!!v)} data-testid="ev-cap-vis" />
                  <span>Show &ldquo;places left&rdquo; lozenge to members</span>
                </label>
              </div>
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard title="Event Host (Stripe account) *" hint="Selects which Stripe pot receives payment for this event." testId="ev-hosts-section">
        <div className="space-y-2">
          {eventHosts.length === 0 ? (
            <div className="text-sm text-muted-foreground">Loading Event Hosts…</div>
          ) : eventHosts.map((h) => (
            <HostRadio key={h.host_id} host={h} active={accountLabel === h.host_id} onClick={() => setAccountLabel(h.host_id)} testId={`ev-acct-${h.host_id}`} />
          ))}
        </div>
        <label className="inline-flex items-center gap-2 text-sm mt-4">
          <Checkbox checked={testMode} onCheckedChange={(v) => setTestMode(!!v)} data-testid="ev-test-mode" />
          <span>
            Test mode — this event uses the Stripe test key (safe for QA). Untick when you&apos;re ready to take real payments.
          </span>
        </label>
      </SectionCard>

      <SectionCard title="Menu selection" testId="ev-menu-section">
        <MenuConfigEditor value={menuConfig} onChange={setMenuConfig} />
      </SectionCard>

      <SectionCard
        title="Optional fields"
        hint="Per-booking fields are answered once per reservation. Per-attendee fields are asked once per guest."
        testId="ev-optional-section"
      >
        <label className="flex items-start gap-2 py-2">
          <Checkbox checked={useBookingCustom} onCheckedChange={(v) => setUseBookingCustom(!!v)} data-testid="ev-use-booking-custom" />
          <div>
            <div className="text-sm font-medium">Per-booking custom fields</div>
            <div className="text-xs text-muted-foreground">Asked once per reservation (e.g. Lodge / Number)</div>
          </div>
        </label>
        {useBookingCustom && (
          <div className="pl-8 space-y-1 mb-2">
            <Label>Per-booking field names (comma separated)</Label>
            <Input value={bookingCustomStr} onChange={(e) => setBookingCustomStr(e.target.value)} placeholder="Lodge / Number" data-testid="ev-booking-cf" />
          </div>
        )}

        <div className="mt-3 text-xs text-muted-foreground uppercase tracking-widest">Per-attendee custom fields</div>
        <div className="text-xs text-muted-foreground mb-2">
          These are asked per guest. Masonic options below pre-fill from each member&apos;s profile.
        </div>
        <div className="pl-2 space-y-1">
          {[
            { key: "mas", state: useMasonicTitle, set: setUseMasonicTitle, label: "Masonic Title", hint: "Bro / W.Bro / R.W.Bro etc. — from profile", testId: "ev-use-masonic-title" },
            { key: "grand", state: useGrandRank, set: setUseGrandRank, label: "Grand Rank", hint: "From profile", testId: "ev-use-grand-rank" },
            { key: "prov", state: useProvincialRank, set: setUseProvincialRank, label: "Provincial Rank", hint: "From profile", testId: "ev-use-provincial-rank" },
            { key: "diet", state: useDietaryConcerns, set: setUseDietaryConcerns, label: "Dietary Concerns", hint: "Allergies / dietary requirements", testId: "ev-use-dietary-concerns" },
            { key: "extra", state: useAttendeeCustom, set: setUseAttendeeCustom, label: "Additional free-form fields", hint: "Extra fields asked per guest, comma separated (e.g. Mobile, Guest of, Notes)", testId: "ev-use-attendee-custom" },
          ].map((row) => (
            <label key={row.key} className="flex items-start gap-2 py-1.5">
              <Checkbox checked={row.state} onCheckedChange={(v) => row.set(!!v)} data-testid={row.testId} />
              <div>
                <div className="text-sm font-medium">{row.label}</div>
                <div className="text-xs text-muted-foreground">{row.hint}</div>
              </div>
            </label>
          ))}
          {useAttendeeCustom && (
            <div className="pl-8 space-y-1 pt-1">
              <Label>Per-attendee field names (comma separated)</Label>
              <Input value={attendeeCustomStr} onChange={(e) => setAttendeeCustomStr(e.target.value)} placeholder="Mobile, Guest of" data-testid="ev-attendee-cf" />
            </div>
          )}
        </div>
      </SectionCard>

      <SectionCard
        title="Complimentary Booking Codes"
        hint="Enable to allow authorised attendees to redeem a per-event 6-character code during booking for a complimentary place. Codes are generated by administrators below."
        testId="ev-complimentary-section"
      >
        <div className="flex gap-2">
          <OptionButton active={!complimentaryEnabled} onClick={() => setComplimentaryEnabled(false)} testId="ev-comp-off">No</OptionButton>
          <OptionButton active={complimentaryEnabled} onClick={() => setComplimentaryEnabled(true)} testId="ev-comp-on">Yes</OptionButton>
        </div>
        {complimentaryEnabled && !isEdit && (
          <div className="text-xs text-muted-foreground mt-3">Codes can be generated after you save this event.</div>
        )}
      </SectionCard>

      {isEdit && complimentaryEnabled && (
        <ComplimentaryCodesPanel eventId={editId} mode="admin" />
      )}

      <div className="flex gap-2 pb-6">
        <Button onClick={save} disabled={saving} data-testid="ev-save">{saving ? "Saving…" : (isEdit ? "Save changes" : "Publish event")}</Button>
        <Button variant="ghost" onClick={() => nav(-1)}>Cancel</Button>
      </div>
    </div>
  );
}
